import './TestimonialsSection.scss';

import React from 'react';
import Markdown from 'markdown-to-jsx';

import {
  Banner,
  BannerDescription,
  BannerImage,
  BannerImageContainer,
  BannerText,
  BannerTextContainer,
  BannerTitle,
  Carousel,
  CarouselSlide,
  FluidImage,
  P,
  ProgressiveImage,
  RatingsStars,
} from '@vp/swan';

import {
  FeatureGroupModel,
  FeatureModel,
} from '@vp/digital-common-data-model-lib';

import MissingContentAlert from '../../common/MissingContentAlert';

interface TestimonialsSectionProps {
  content: FeatureGroupModel;
  accessibility: FeatureGroupModel;
}

const TestimonialsSection = ({
  content,
  accessibility,
}: TestimonialsSectionProps) => {
  if (!content) {
    return <MissingContentAlert />;
  }

  return (
    <Carousel
      accessibleTextForNext={accessibility.features[4].title}
      accessibleTextForPrevious={accessibility.features[3].title}
      aria-roledescription={accessibility.features[0].title}
      aria-label={content.title}
      accessibleTextForDots={
        content.features.length > 0
          ? content.features.map(
              (feature, index) =>
                accessibility.features[5]?.title.replace(
                  '{{SLIDE_NUMBER}}',
                  String(index + 1)
                ) ?? ''
            )
          : []
      }
      accessibleTextForVisibleSlides={accessibility.features[2]?.hoverText}
      accessibleTextForSlide={accessibility.features[2]?.altText}
      slidesToShow={1}
      dots={false}
      slidesToScroll={1}
      arrows={false}
    >
      {content.features.map((feature: FeatureModel, index: React.Key) => (
        <CarouselSlide key={index}>
          <Banner
            backgroundColor={'warm-white'}
            variant="full-width-image"
            className="wix-websites-pdp__testimonials__banner"
          >
            <BannerTextContainer className="wix-websites-pdp__testimonials__text-container">
              <BannerText
                style={{ opacity: 0.9 }}
                textAlign={'left'}
                p={{ xs: 7 }}
              >
                <blockquote
                  style={{ margin: 0 }}
                  cite={feature.description || undefined}
                >
                  <BannerTitle fontSize="x2large">{feature.title}</BannerTitle>
                </blockquote>
                <BannerDescription>
                  <RatingsStars
                    ratingValue={5}
                    hideRating
                    aria-label="Rating: 5 out of 5 stars"
                    className="wix-websites-pdp__testimonials__banner__accessible-rating-stars"
                  />

                  <P>
                    <Markdown>{feature.description}</Markdown>
                  </P>
                </BannerDescription>
              </BannerText>
            </BannerTextContainer>

            <BannerImageContainer>
              <BannerImage>
                <ProgressiveImage
                  placeholder={feature.icons[0].placeholderUrl}
                  srcSet={feature.icons[0].resourceSetUrl}
                  src={feature.icons[0].secureUrl}
                  sizes="(max-width: 2702px) 100vw, 2702px"
                >
                  {(image) => (
                    <FluidImage
                      alt={feature.altText ?? feature.title}
                      srcSet={image.srcSet}
                      src={image.src}
                      sizes={image.sizes}
                    />
                  )}
                </ProgressiveImage>
              </BannerImage>
            </BannerImageContainer>
          </Banner>
        </CarouselSlide>
      ))}
    </Carousel>
  );
};

export default TestimonialsSection;
